import { useEffect, useState } from 'react'
import { adminApi, getApiErrorMessage } from '../services/adminApi'
import { DataTable } from '../components/DataTable'
import { Alert, Badge, Button, Card, PageHeader, Spinner, StatCard } from '../components/ui'
import { formatBytes, formatDate, formatUptime } from '../utils/format'

function ServiceBadge({ status, message }) {
  const tone = status === 'ok' ? 'success' : status === 'error' ? 'danger' : 'warning'
  return (
    <div className="flex items-center justify-between rounded-lg border border-slate-100 bg-slate-50 px-4 py-3">
      <span className="text-sm font-medium text-slate-700">{message}</span>
      <Badge tone={tone}>{status === 'ok' ? 'OK' : 'Atenção'}</Badge>
    </div>
  )
}

export default function Monitoring() {
  const [current, setCurrent] = useState(null)
  const [system, setSystem] = useState(null)
  const [tenants, setTenants] = useState([])
  const [logs, setLogs] = useState([])
  const [history, setHistory] = useState([])
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(true)

  const load = async () => {
    setLoading(true)
    setError('')
    try {
      const [c, s, t, l, h] = await Promise.all([
        adminApi.monitoringCurrent(),
        adminApi.monitoringSystem(),
        adminApi.monitoringTenants(),
        adminApi.monitoringLogs(40),
        adminApi.monitoringHistory(),
      ])
      setCurrent(c.data.data)
      setSystem(s.data.data)
      setTenants(t.data.data || [])
      setLogs(l.data.data || [])
      setHistory(h.data.data || [])
    } catch (err) {
      setError(getApiErrorMessage(err))
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    load()
  }, [])

  const forceSave = async () => {
    try {
      await adminApi.monitoringForceSave()
      load()
    } catch (err) {
      setError(getApiErrorMessage(err))
    }
  }

  if (loading) return <Spinner />

  const services = current?.services || {}

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="Operação"
        title="Monitoramento"
        description={`Saúde dos serviços e infraestrutura. Atualizado: ${formatDate(current?.timestamp)}`}
        icon="fa-heart-pulse"
        actions={
          <>
          <Button variant="outline" onClick={load}>
            Atualizar
          </Button>
          <Button onClick={forceSave}>Forçar snapshot</Button>
          </>
        }
      />

      {error && <Alert type="error">{error}</Alert>}

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          label="Bases (tenants)"
          value={current?.db?.tenantsCount ?? '—'}
          sub={`Tamanho total: ${formatBytes(current?.db?.totalSize)}`}
          icon="fa-database"
        />
        <StatCard
          label="Fotos (7 dias)"
          value={current?.photos?.totalLastWeek ?? 0}
          sub="Novos uploads na semana"
          icon="fa-images"
          tone="green"
        />
        <StatCard
          label="Uptime Node"
          value={formatUptime(services?.system?.uptime ?? system?.uptime)}
          sub={`RAM processo: ${formatBytes((services?.system?.memoryUsage || system?.memory?.rss_mb * 1024 * 1024))}`}
          icon="fa-clock"
          tone="cyan"
        />
        <StatCard
          label="Conexões DB"
          value={services?.database?.activeConnections ?? '—'}
          sub={services?.database?.message}
          icon="fa-link"
          tone="amber"
        />
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card title="Status dos serviços">
          <div className="space-y-2">
            <ServiceBadge status={services?.database?.status} message="Banco de dados" />
            <ServiceBadge
              status={services?.email?.status}
              message={`E-mail${services?.email?.queueSize != null ? ` · fila: ${services.email.queueSize}` : ''}`}
            />
            <ServiceBadge status={services?.system?.status} message="Processo Node.js" />
          </div>
        </Card>

        <Card title="Servidor (SO)">
          {system ? (
            <dl className="grid grid-cols-2 gap-3 text-sm">
              <div>
                <dt className="text-slate-500">Node</dt>
                <dd className="font-medium">{system.node_version}</dd>
              </div>
              <div>
                <dt className="text-slate-500">Plataforma</dt>
                <dd className="font-medium">{system.os?.platform}</dd>
              </div>
              <div>
                <dt className="text-slate-500">RAM livre</dt>
                <dd className="font-medium">{system.os?.free_mb} MB</dd>
              </div>
              <div>
                <dt className="text-slate-500">RAM em uso</dt>
                <dd className="font-medium">{system.os?.used_pct}%</dd>
              </div>
              <div>
                <dt className="text-slate-500">Heap</dt>
                <dd className="font-medium">
                  {system.memory?.heap_used_mb} / {system.memory?.heap_total_mb} MB
                </dd>
              </div>
              <div>
                <dt className="text-slate-500">Load avg</dt>
                <dd className="font-mono text-xs">
                  {(system.os?.load_avg || []).map((n) => n.toFixed(2)).join(' · ')}
                </dd>
              </div>
            </dl>
          ) : (
            <p className="text-sm text-slate-500">Sem dados do sistema.</p>
          )}
        </Card>
      </div>

      <Card title="Bancos de dados">
        <DataTable
          columns={[
            { key: 'name', label: 'Nome' },
            { key: 'id', label: 'ID' },
            {
              key: 'sizeBytes',
              label: 'Tamanho',
              render: (r) => formatBytes(r.sizeBytes),
            },
            {
              key: 'isTenant',
              label: 'Tipo',
              render: (r) => (r.isTenant ? 'Tenant' : 'Global'),
            },
          ]}
          rows={current?.db?.details || []}
        />
      </Card>

      <Card title="Clientes (tenants)">
        <DataTable
          columns={[
            { key: 'tenant_id', label: 'ID' },
            { key: 'company_name', label: 'Empresa' },
            {
              key: 'status',
              label: 'Status',
              render: (r) => (
                <Badge tone={r.status === 'active' ? 'success' : 'danger'}>{r.status}</Badge>
              ),
            },
            { key: 'persons', label: 'Pessoas' },
            { key: 'equipments', label: 'Equip.' },
            {
              key: 'db_size',
              label: 'DB',
              render: (r) => formatBytes(r.db_size),
            },
            {
              key: 'last_access',
              label: 'Último acesso',
              render: (r) => formatDate(r.last_access),
            },
            { key: 'face_queue_pending', label: 'Fila faces' },
            { key: 'sync_queue_pending', label: 'Fila sync' },
          ]}
          rows={tenants}
        />
      </Card>

      <Card title="Logs recentes (erros e avisos)">
        <DataTable
          columns={[
            {
              key: 'timestamp',
              label: 'Data',
              render: (r) => formatDate(r.timestamp),
            },
            {
              key: 'level',
              label: 'Nível',
              render: (r) => (
                <Badge tone={r.level === 'error' ? 'danger' : 'warning'}>{r.level}</Badge>
              ),
            },
            {
              key: 'message',
              label: 'Mensagem',
              render: (r) => (
                <span className="line-clamp-2 max-w-xl" title={r.message}>
                  {r.message}
                </span>
              ),
            },
          ]}
          rows={logs}
          emptyMessage="Nenhum erro ou aviso recente no log."
        />
      </Card>

      <Card title="Histórico (snapshots)">
        <DataTable
          columns={[
            {
              key: 'timestamp',
              label: 'Data',
              render: (r) => formatDate(r.timestamp),
            },
            {
              key: 'tenants',
              label: 'Tenants',
              render: (r) => r.db?.tenantsCount ?? '—',
            },
            {
              key: 'dbSize',
              label: 'Tamanho DBs',
              render: (r) => formatBytes(r.db?.totalSize),
            },
            {
              key: 'photos',
              label: 'Fotos (7d)',
              render: (r) => r.photos?.totalLastWeek ?? 0,
            },
          ]}
          rows={history}
          emptyMessage="Nenhum snapshot salvo. Use «Forçar snapshot» para registrar."
        />
      </Card>
    </div>
  )
}
